import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Table, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { cookie } from 'utils/cookies';

const CanopyForm = ({ role = cookie.get('user-role') }) => {
  const dispatch = useDispatch();
  const { viewFormData } = useSelector((state) => state.checklist);
  const { formId, stationId } = useParams();
  const navigate = useNavigate();

  const sigCanvas = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [currentSignature, setCurrentSignature] = useState({ role: '' });
  const [isLoading, setIsLoading] = useState(true);
  const dataLoadedRef = useRef(false);

  const { register, control, handleSubmit, setValue } = useForm({
    defaultValues: {
      stationId: stationId || '',
      project_name: '',
      inspection_date: '',
      vendor_name: '',
      canopy_size: '',
      canopy_serial_number: '',
      equipment_tag: '',

      visual_checks: Array(32).fill({ status: '', remark: '' }),
      physical_checks: Array(7).fill({ status: '', remark: '' }),
      corrective_actions: Array(5).fill({ issue: '', action: '', target_date: '' }),

      canopy_found_okay: '',
      summary_remarks: '',
      other_comments: '',
      
      comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      status: 'draft',
    },
  });

  const formValues = useWatch({ control });
  const statusWatch = useWatch({ control, name: 'status' });
  const manager_id = useWatch({ control, name: 'manager_id' });

  const autoSave = React.useCallback(
    _.debounce((data) => {
      if (!dataLoadedRef.current) return;
      const payload = {
        ...data,
        form_id: formId,
        formName: 'canopy',
      };
      dispatch({ type: SUBMIT_FORM, payload: { data: payload } });
    }, 2000),
    [dispatch, formId]
  );

  useEffect(() => {
    autoSave(formValues);
  }, [formValues, autoSave]);

  useEffect(() => {
    if (formId) {
      dispatch({ type: VIEW_FORM, payload: { formId, formName: 'canopy' } });
    }
    setIsLoading(false);
  }, [dispatch, formId]);

  useEffect(() => {
    if (viewFormData) {
      const d = viewFormData || {};
      setValue('stationId', d.stationId || stationId || '');
      setValue('project_name', d.project_name || '');
      setValue('inspection_date', d.inspection_date || '');
      setValue('vendor_name', d.vendor_name || '');
      setValue('canopy_size', d.canopy_size || '');
      setValue('canopy_serial_number', d.canopy_serial_number || '');
      setValue('equipment_tag', d.equipment_tag || '');

      setValue('visual_checks', Array.isArray(d.visual_checks) ? d.visual_checks : Array(32).fill({ status: '', remark: '' }));
      setValue('physical_checks', Array.isArray(d.physical_checks) ? d.physical_checks : Array(7).fill({ status: '', remark: '' }));
      setValue('corrective_actions', Array.isArray(d.corrective_actions) ? d.corrective_actions : Array(5).fill({ issue: '', action: '', target_date: '' }));

      setValue('canopy_found_okay', d.canopy_found_okay || '');
      setValue('summary_remarks', d.summary_remarks || '');
      setValue('other_comments', d.other_comments || '');
      setValue('comments', d.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('status', d.status || 'draft');
      setValue('manager_id', d.l1?.managerId || d.managerId || null);
      dataLoadedRef.current = true;
    }
  }, [viewFormData, stationId, setValue]);

  const fieldsEditable = (role === 'engineer' && (statusWatch === 'draft' || statusWatch === 'CONTRACTOR' || statusWatch === 'MANAGER')) || (role === 'contractor' && (statusWatch === 'draft' || statusWatch === 'CONTRACTOR'));
  const canComment = (role === 'engineer' && (statusWatch === 'draft' || statusWatch === 'CONTRACTOR' || statusWatch === 'MANAGER')) || (role === 'contractor' && (statusWatch === 'draft' || statusWatch === 'CONTRACTOR')) || (role === 'manager' && statusWatch === 'MANAGER') || (role === 'quality' && statusWatch === 'QUALITY');
  const isSigEditable = (sigRole) => {
    if (manager_id) return false;
    return (
      (role === 'engineer' && sigRole === 'charge_engineer' && (statusWatch === 'draft' || statusWatch === 'CONTRACTOR' || statusWatch === 'MANAGER')) ||
      (role === 'contractor' && sigRole === 'contractor' && (statusWatch === 'draft' || statusWatch === 'CONTRACTOR')) ||
      (role === 'quality' && sigRole === 'quality' && statusWatch === 'QUALITY')
    );
  };

  const handleImageUpload = (e, sigRole) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 0.05 * 1024 * 1024) {
        alert('File size exceeds 50kb.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => setValue(`comments.${sigRole}.signature`, reader.result, { shouldDirty: true });
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureTypeChange = (sigRole, type) => {
    setValue(`comments.${sigRole}.signatureType`, type, { shouldDirty: true });
    setValue(`comments.${sigRole}.signature`, '', { shouldDirty: true });
    if (type === 'digital') {
      setCurrentSignature({ role: sigRole });
      setShowModal(true);
    }
  };

  const handleSaveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setValue(`comments.${currentSignature.role}.signature`, sigCanvas.current.toDataURL(), { shouldDirty: true });
    }
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const onSubmit = (action) => (data) => {
    let newStatus = data.status;
    if (role === 'engineer') newStatus = 'CONTRACTOR';
    else if (role === 'contractor') newStatus = 'MANAGER';
    else if (role === 'manager') newStatus = action === 'approve' ? 'QUALITY' : 'draft';
    else if (role === 'quality') newStatus = action === 'approve' ? 'approved' : 'draft';

    const payload = { ...data, status: newStatus, form_id: formId, formName: 'canopy' };
    dispatch({ type: SUBMIT_FORM, payload: { data: payload } });
    toast.success('Submitted Successfully');
    navigate(-1);
  };

  const renderCheckRow = (desc, index, prefix = 'visual_checks') => (
    <tr key={index}>
      <td>{index + 1}</td>
      <td>{desc}</td>
      <td>
        {['Yes', 'No', 'NA'].map(v => (
          <Form.Check inline key={v} label={v} type="radio" value={v} {...register(`${prefix}.${index}.status`)} disabled={!fieldsEditable} />
        ))}
      </td>
      <td><Form.Control type="text" {...register(`${prefix}.${index}.remark`)} disabled={!fieldsEditable} /></td>
    </tr>
  );

  const visualCheckItems = [
    'Is the baseplate free from rust, corrosion, or cracks?',
    'Is the J-bolt cap in place and properly secured?',
    'Are there any cracks, bends, or deformation in the frame?',
    'Are all welded joints and connections intact?',
    'Is there any rust, corrosion, or paint chipping on the frame?',
    'Are there any tears, holes, or sagging in the fabric or roofing material?',
    'Is there any discoloration, UV damage, or aging?',
    'Is there any shifting, leaning, or instability in the structure?',
    'Are the ACP sheets free from dents or peeling?',
    'Are there any scratches, wear, or damage on the ACP sheets?',
    'Are the ACP sheet joints properly sealed?',
    'Is there any water pooling on the canopy surface?',
    'Are drainage channels or gutters free from blockages?',
    'Are the downspouts unblocked?',
    'Are lighting fixtures free from damage or exposed wiring?',
    'Is cable management neat and secure?',
    'Is the waterproofing of electrical fixtures intact?',
    'Is the CCTV installed at the correct location as per the drawing?',
    'Is the CCTV power cable routed through a conduit?',
    'Is the CCTV LAN cable routed through a conduit?',
    'Is the router positioned and fixed according to the drawing?',
    'Is the area free from dirt, stains, or oil residues?',
    'Is the surface free from graffiti, stickers, or unwanted markings?',
    'Are all signs, logos, and branding intact?',
    'Is the signage legible and clearly visible?',
    'Is the local language branding correctly placed?',
    'Are the information hoardings in good condition?',
    'Are the bollards and car stoppers properly aligned?',
    'Are there any cracks, rust, or impact damage visible?',
    'Is there any soil erosion, uneven ground, or standing water?',
    'Is proper drainage maintained around the canopy?',
    'Are the surrounding areas free of obstructions or safety hazards?',
  ];

  const physicalCheckItems = [
    'Are there any loose or missing bolts and fasteners?',
    'Are all bolts, nuts, and fasteners properly tightened?',
    'Is the roofing material properly installed?',
    'Is there any shifting, leaning, or instability in the structure?',
    'Are the waterproofing materials intact?',
    'Is the waterproofing of electrical fixtures intact?',
    'Are the foundations of bollards and car stoppers secure?',
  ];

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="page-body container-xl">
      <h2 className="page-title mb-4">Canopy - Site Acceptance Testing</h2>
      <Form>
        <h3>1. Equipment Details</h3>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3"><Form.Label>Station ID</Form.Label><Form.Control type="text" {...register('stationId')} readOnly /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Project Name</Form.Label><Form.Control type="text" {...register('project_name')} disabled={!fieldsEditable} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Inspection Date</Form.Label><Form.Control type="date" {...register('inspection_date')} disabled={!fieldsEditable} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Vendor Name</Form.Label><Form.Control type="text" {...register('vendor_name')} disabled={!fieldsEditable} /></Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3"><Form.Label>Canopy Size</Form.Label><Form.Control type="text" {...register('canopy_size')} disabled={!fieldsEditable} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Canopy Serial Number</Form.Label><Form.Control type="text" {...register('canopy_serial_number')} disabled={!fieldsEditable} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Equipment Tag</Form.Label><Form.Control type="text" {...register('equipment_tag')} disabled={!fieldsEditable} /></Form.Group>
          </Col>
        </Row>

        <h3 className="mt-4">2. Visual Checks</h3>
        <Table bordered responsive>
          <thead><tr><th>Sr.</th><th>Checklist Item</th><th>Status</th><th>Remarks</th></tr></thead>
          <tbody>{visualCheckItems.map((item, i) => renderCheckRow(item, i, 'visual_checks'))}</tbody>
        </Table>

        <h3 className="mt-4">3. Physical Checks</h3>
        <Table bordered responsive>
          <thead><tr><th>Sr.</th><th>Checklist Item</th><th>Status</th><th>Remarks</th></tr></thead>
          <tbody>{physicalCheckItems.map((item, i) => renderCheckRow(item, i, 'physical_checks'))}</tbody>
        </Table>

        <h3 className="mt-4">Corrective and Preventive Action Table</h3>
        <Table bordered responsive>
          <thead><tr><th>Sr.</th><th>Identified Issue</th><th>Corrective Action</th><th>Target Completion Date</th></tr></thead>
          <tbody>
            {Array(5).fill(0).map((_, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td><Form.Control type="text" {...register(`corrective_actions.${i}.issue`)} disabled={!fieldsEditable} /></td>
                <td><Form.Control type="text" {...register(`corrective_actions.${i}.action`)} disabled={!fieldsEditable} /></td>
                <td><Form.Control type="date" {...register(`corrective_actions.${i}.target_date`)} disabled={!fieldsEditable} /></td>
              </tr>
            ))}
          </tbody>
        </Table>

        <h3 className="mt-4">Inspection Summary</h3>
        <Form.Group className="mb-3">
          <Form.Label>Canopy Found Okay</Form.Label>
          <div>
            {['Yes', 'No'].map(v => (
              <Form.Check inline key={v} label={v} type="radio" value={v} {...register('canopy_found_okay')} disabled={!fieldsEditable} />
            ))}
          </div>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Remarks</Form.Label>
          <Form.Control as="textarea" rows={3} {...register('summary_remarks')} disabled={!fieldsEditable} />
        </Form.Group>

        <h3 className="mt-4">4. Other Comments / Remarks</h3>
        <Form.Group className="mb-3">
          <Form.Control as="textarea" rows={4} {...register('other_comments')} disabled={!canComment} />
        </Form.Group>

        <h3 className="mt-4">5. Comments & Signatures</h3>
        <Table bordered responsive>
          <thead><tr><th></th><th>Engineer - Contractor</th><th>Engineer - Charge Zone</th><th>Quality - Charge Zone</th><th>Optional</th></tr></thead>
          <tbody>
            {['Name', 'Company', 'Signature', 'Date'].map(field => (
              <tr key={field}>
                <td>{field}</td>
                {['contractor', 'charge_engineer', 'quality', 'optional'].map(sigRole => {
                    const editable = isSigEditable(sigRole);
                    if (field === 'Date') return <td key={sigRole}><Form.Control type="date" {...register(`comments.${sigRole}.date`)} disabled={!editable} /></td>;
                    if (field === 'Signature') return <td key={sigRole}>
                      <Form.Select size="sm" {...register(`comments.${sigRole}.signatureType`)} onChange={(e) => handleSignatureTypeChange(sigRole, e.target.value)} disabled={!editable} className="mb-2">
                        <option value="upload">Upload</option><option value="digital">Digital</option>
                      </Form.Select>
                      {_.get(formValues, `comments.${sigRole}.signatureType`) === 'upload' && (
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, sigRole)}
                          className="mb-2"
                          disabled={!editable}
                        />
                      )}
                      {_.get(formValues, `comments.${sigRole}.signature`) && <img src={_.get(formValues, `comments.${sigRole}.signature`)} alt="sig" style={{ maxWidth: '80px', display: 'block' }} />}
                    </td>;
                    return <td key={sigRole}><Form.Control type="text" {...register(`comments.${sigRole}.${field.toLowerCase()}`)} disabled={!editable} /></td>;
                })}
              </tr>
            ))}
          </tbody>
        </Table>

        <div className="mt-4 mb-5">
            {role === 'manager' && statusWatch === 'MANAGER' ? (
                <><Button variant="success" onClick={handleSubmit(onSubmit('approve'))}>Approve</Button> <Button variant="danger" onClick={handleSubmit(onSubmit('rework'))}>Rework</Button></>
            ) : role === 'quality' && statusWatch === 'QUALITY' ? (
                <><Button variant="success" onClick={handleSubmit(onSubmit('approve'))}>Approve</Button> <Button variant="danger" onClick={handleSubmit(onSubmit('rework'))}>Rework</Button></>
            ) : (
                <><Button variant="success" onClick={handleSubmit(onSubmit('pending'))} disabled={!fieldsEditable}>Submit</Button> <Button variant="secondary" onClick={handleSubmit(onSubmit('draft'))} disabled={!fieldsEditable}>Save Draft</Button></>
            )}
        </div>
      </Form>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton><Modal.Title>Signature</Modal.Title></Modal.Header>
        <Modal.Body>
          <SignatureCanvas ref={sigCanvas} penColor="black" canvasProps={{ width: 450, height: 200, className: 'sigCanvas border' }} />
          <Button className="mt-2" onClick={handleSaveSignature}>Save</Button>
        </Modal.Body>
      </Modal>
    </div>
  );
};

CanopyForm.propTypes = {
  role: PropTypes.string,
};

export default CanopyForm;
